import { Box, Typography, TextField, Button, Stack, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import NoData from "../../no-data";
import { TagsService } from "../../../services/tags-service";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import { Tag } from "../../../types/tags.types";
import { toast } from "react-toastify";
import { When } from "react-if";

interface TagsSectionProps {
    onSelectedTagsChange?: (tags: Tag[]) => void;
    selectedTags: Tag[]
    showDesc?: boolean;
}


const tagColors = [
    "#F44336", "#E91E63", "#9C27B0", "#673AB7", "#3F51B5",
    "#2196F3", "#03A9F4", "#00BCD4", "#009688", "#4CAF50",
    "#8BC34A", "#CDDC39", "#FFEB3B", "#FFC107", "#FF9800",
    "#FF5722", "#795548", "#9E9E9E", "#607D8B", "#B71C1C",
    "#880E4F", "#4A148C", "#311B92", "#1A237E", "#0D47A1",
    "#01579B", "#006064", "#004D40", "#1B5E20", "#33691E",
    "#827717", "#F57F17", "#FF6F00", "#E65100", "#BF360C",
    "#3E2723", "#263238", "#546E7A", "#37474F", "#F06292",
    "#BA68C8", "#9575CD", "#7986CB", "#64B5F6", "#4DD0E1",
    "#4DB6AC", "#81C784", "#AED581", "#DCE775", "#FFF176"
];

const TagsSection: React.FC<TagsSectionProps> = ({ onSelectedTagsChange, selectedTags, showDesc = true }) => {
    const [searchValue, setSearchValue] = useState("");
    const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const {
        items: availableTags,
    } = useSelector((state: RootState) => state.tags);

    

    const toggleTag = (tag: Tag) => {
        let updatedTags;
        if (selectedTags.some((t) => t._id === tag._id)) {
            updatedTags = selectedTags.filter((t) => t._id !== tag._id);
        } else {
            updatedTags = [...selectedTags, tag];
        }
        if (onSelectedTagsChange) {
            onSelectedTagsChange(updatedTags);
        }
    };

    // Deduplicate tags by _id, then sort
    const deduped = availableTags.filter(
        (tag: Tag, idx: number, arr: Tag[]) => arr.findIndex((t) => t._id === tag._id) === idx
    );
    const sortedTags = deduped.slice().sort((a: Tag, b: Tag) => a.name.localeCompare(b.name));
    let unselectedTags = sortedTags.filter(tag => !selectedTags.find(t => t._id === tag._id));
    if(searchValue !== "") {
        unselectedTags = unselectedTags.filter(tag =>
            tag.name.toLowerCase().startsWith(searchValue.toLowerCase())
        );
    }
    const [hoveredTagId, setHoveredTagId] = useState<string | null>(null);
    return (
        <Box display="flex" flexDirection="column" gap={1} width="100%">
            {/* Tags Section */}
            <When condition={showDesc}>
                <Typography fontSize={24} fontWeight={600}>Tags</Typography>
                <Typography variant="body2" color="textSecondary">
                    Tagged are global attributes of a group or team. Search for tags or select from the list of available tags below.
                    If you want to create a new tag, click the "+ Add New Tag" button below.
                </Typography>
            </When>

            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search Tag"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
            />

            {/* Available Tags */}
            <Box
                sx={{
                    maxHeight: '140px',
                    overflowY: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    padding: '8px',
                    justifyContent: 'centered'
                }}
            >
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    justifyContent='center'
                    gap={1}>
                    {unselectedTags.length > 0 ?
                    unselectedTags.map((tag) => (
                        <Box
                            key={tag._id}
                            display="flex"
                            alignItems="center"
                            onClick={() => toggleTag(tag)}
                            onMouseEnter={() => setHoveredTagId(tag._id!)}
                            onMouseLeave={() => setHoveredTagId(null)}
                            sx={{
                                cursor: "pointer",
                                fontSize: "14px",
                                padding: "6px 12px",
                                border: selectedTags.some(t => t._id === tag._id) ? "1px solid #1976d2" : "1px solid #ccc",
                                borderRadius: "16px",
                                backgroundColor: tag.color,
                                color:  "white",
                                transition: "all 0.2s",
                                "&:hover": {
                                    backgroundColor: selectedTags.some(t => t._id === tag._id) ? "#1565c0" : "#f0f0f0",
                                    color: "black",

                                },
                            }}
                        >
                            {tag.name}
                            {hoveredTagId === tag._id && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        console.log('delete TAgg')
                                        setTagToDelete(tag);
                                        setConfirmOpen(true);
                                    }}
                                >
                                    <CloseIcon
                                        fontSize="small"
                                        sx={{
                                        ml: 1,
                                        transition: "opacity 0.2s",
                                        }}
                                        
                                    />
                            </Box>
                            
                        )}
                        </Box>
                    )) : <NoData
                            title="No Tags available"
                            description="Create more tags"
                            buttonText={null}
                            onClick={() => { }}
                        />}
                </Stack>
            </Box>
            {/* Add New Tag Button */}
            <AddTagButton onCreate={(name, color) => {
                console.log(name, color)
                TagsService.createTag({ name, color}, (isValid, message) => {})
            }}/>

            {/* Selected Tags */}
            <Typography
                fontSize={20}
                fontWeight={600}
            >
                Selected Tags
            </Typography>
            <Box
                display="flex"
                flexWrap="wrap"
                gap={1}
                p={2}
                border="1px solid #ddd"
                borderRadius={1}
            >
                {selectedTags.map((tag) => (
                    <Box
                        key={tag._id}
                        display="flex"
                        alignItems="center"
                        onMouseEnter={() => setHoveredTagId(tag._id!)}
                        onMouseLeave={() => setHoveredTagId(null)}
                        onClick={() => toggleTag(tag)}
                        sx={{
                            padding: "6px 12px",
                            border: "1px solid grey",
                            borderRadius: "16px",
                            backgroundColor: tag.color,
                            color: "white",
                            cursor: "pointer",
                            "&:hover": {
                                backgroundColor: "#f0f0f0",
                                color: "black",
                            },
                        }}
                    >
                        <Typography variant="body2" mr={hoveredTagId === tag._id ? 1 : 0}>
                            {tag.name}
                        </Typography>
                        {hoveredTagId === tag._id && (
                            <CloseIcon
                                fontSize="small"
                                sx={{
                                ml: 1,
                                transition: "opacity 0.2s",
                                }}
                            />
                            
                        )}
                    </Box>
                ))}
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Delete Tag</DialogTitle>
                <DialogContent>
                    <Typography>
                    Are you sure you want to delete the tag{" "}
                    <strong>{tagToDelete?.name}</strong>? This will also remove it from all groups and teams it is associated with.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button
                    color="error"
                    onClick={() => {
                        if (tagToDelete?._id) {
                        TagsService.deleteTag(tagToDelete._id, (isValid, msg) => {
                            if (isValid) {
                            toast.success("Tag deleted successfully.");
                            } else {
                            toast.error(msg ?? "Failed to delete tag.");
                            }
                            setConfirmOpen(false);
                        });
                        }
                    }}
                    >
                    Delete
                    </Button>
                </DialogActions>
                </Dialog>

        </Box>
        
    );
};

const AddTagButton = ({ onCreate }: { onCreate: (name: string, color: string) => void }) => {
    const [open, setOpen] = useState(false);
    const [tagName, setTagName] = useState("");
    const [selectedColor, setSelectedColor] = useState(tagColors[0]);

    const handleSubmit = () => {
        if (!tagName.trim()) return;
        onCreate(tagName.trim(), selectedColor);
        setTagName("");
        setSelectedColor(tagColors[0]);
        setOpen(false);
    };

    return (
        <>
            <Button
                sx={{ width: "200px" }}
                startIcon={<AddIcon />}
                variant="text"
                onClick={() => setOpen(true)}
            >
                Add New Tag
            </Button>

            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Create New Tag</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Tag Name"
                        value={tagName}
                        onChange={(e) => setTagName(e.target.value)}
                        fullWidth
                        sx={{ mt: 1, mb: 3 }}
                    />

                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {tagColors.map((color) => (
                            <Box
                                key={color}
                                onClick={() => setSelectedColor(color)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: color,
                                    border: color === selectedColor ? "2px solid black" : "1px solid #ccc",
                                    cursor: "pointer",
                                }}
                            />
                        ))}
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TagsSection;
